import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, CreditCard, Hotel, BookOpen, Send, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForAuthors = () => {
  const [paperFormData, setPaperFormData] = useState({
    title: '',
    abstract: '',
    authors: '',
    email: '',
    affiliation: '',
    track: '',
    keywords: '',
  });

  const [regFormData, setRegFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    category: '',
    affiliation: '',
    country: '',
    dietary_requirements: '',
  });

  const [isSubmittingPaper, setIsSubmittingPaper] = useState(false);
  const [isSubmittingReg, setIsSubmittingReg] = useState(false);

  const handlePaperChange = (e) => {
    setPaperFormData({ ...paperFormData, [e.target.name]: e.target.value });
  };

  const handleRegChange = (e) => {
    setRegFormData({ ...regFormData, [e.target.name]: e.target.value });
  };

  const handlePaperSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPaper(true);
    try {
      await axios.post(`${API}/papers/submit`, paperFormData);
      toast.success('Paper submitted successfully!');
      setPaperFormData({ title: '', abstract: '', authors: '', email: '', affiliation: '', track: '', keywords: '' });
    } catch (error) {
      toast.error('Failed to submit paper. Please try again.');
    } finally {
      setIsSubmittingPaper(false);
    }
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingReg(true);
    try {
      await axios.post(`${API}/registration`, regFormData);
      toast.success('Registration successful!');
      setRegFormData({ full_name: '', email: '', phone: '', category: '', affiliation: '', country: '', dietary_requirements: '' });
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmittingReg(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  const tracks = [
    'Edge AI and Real-Time Automation',
    'AI-Powered Autonomous Systems',
    'AI and Robotics',
    'Smart Manufacturing & Industry 5.0',
    'Intelligent Systems for Sustainability',
    'Digital Twins',
    'AI for Green Automation',
    'Smart Materials',
  ];

  const registrationFees = [
    { category: 'UG/PG Students (India)', fee: '₹7,000' },
    { category: 'Academicians (India)', fee: '₹8,000' },
    { category: 'Industry Professionals', fee: '₹9,000' },
    { category: 'Foreign Author (Faculty/Industry)', fee: 'USD 200' },
    { category: 'Foreign Author (Students)', fee: 'USD 150' },
    { category: 'Abstract Presentation Only (India)', fee: '₹5,000' },
    { category: 'Abstract Presentation Only (Foreign)', fee: 'USD 100' },
  ];

  return (
    <div data-testid="for-authors-page">
      {/* Hero Section */}
      <section className="py-12 md:py-12 mesh-gradient relative overflow-hidden" data-testid="authors-hero">
        <div className="absolute inset-0 bg-black/5 grid-pattern"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 bg-[#B6671D]/10 text-[#B6671D]-dark rounded-full px-4 py-2 mb-6">
              <BookOpen size={20} />
              <span className="font-inter text-sm font-medium">Author Resources</span>
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-950 mb-6 tracking-tight">
              For <span className="text-[#B6671D]">Authors</span>
            </h1>
            <p className="font-inter text-lg text-slate-600 leading-relaxed">
              Everything you need to submit your paper, register for the conference, and prepare for your presentation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 bg-white" data-testid="authors-tabs">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Tabs defaultValue="guidelines" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 bg-slate-100 p-2 rounded-lg h-auto">
              <TabsTrigger value="guidelines" className="font-inter py-3 data-[state=active]:bg-white">
                <FileText className="mr-2" size={18} />
                Submission Guidelines
              </TabsTrigger>
              <TabsTrigger value="registration" className="font-inter py-3 data-[state=active]:bg-white">
                <CreditCard className="mr-2" size={18} />
                Registration
              </TabsTrigger>
              <TabsTrigger value="brochure" className="font-inter py-3 data-[state=active]:bg-white">
                <Download className="mr-2" size={18} />
                Conference Brochure
              </TabsTrigger>
              <TabsTrigger value="accommodation" className="font-inter py-3 data-[state=active]:bg-white">
                <Hotel className="mr-2" size={18} />
                Accommodation
              </TabsTrigger>
            </TabsList>

            {/* Submission Guidelines Tab */}
            <TabsContent value="guidelines" className="mt-8">
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

  {/* Guidelines */}
  <motion.div {...fadeInUp}>
    <h2 className="font-outfit text-3xl font-bold text-slate-950 mb-6">
      Submission Guidelines
    </h2>

    <div className="space-y-6 font-inter text-slate-600 leading-relaxed">
      <p>
        We invite researchers, academicians, and industry professionals to submit original research papers,
        review articles, and case studies for presentation at IRASM-2027.
      </p>

      <div className="bg-[#B6671D]/10 border-l-4 border-[#B6671D] p-6 rounded-r-lg">
        <p className="font-semibold text-slate-900 mb-2">Publication</p>
        <p>
          Accepted papers will be published in conference proceedings indexed in 
          <strong> Web of Science, Scopus, and Inspec</strong>.
        </p>
      </div>

      <div>
        <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-3">
          Paper Format
        </h4>

        <ul className="space-y-2">
          {[
            "Papers should be 6–8 pages in IEEE format",
            "Include abstract (250 words max) and keywords",
            "Submit in PDF format only",
            "All papers undergo double-blind peer review"
          ].map((item, index) => (
            <li key={index} className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-[#B6671D] rounded-full mt-2"></div>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>

  {/* Submission Info */}
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: 0.2 }}
  >
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 h-full flex flex-col justify-between">

      <div>
        <h3 className="font-outfit text-2xl font-bold text-slate-950 mb-4">
          Submit Your Paper
        </h3>

        <p className="text-slate-600 mb-4">
          Authors are requested to submit their full-length papers through the official submission system.
        </p>

        <div className="bg-[#B6671D]/10 p-4 rounded-lg mb-4">
          <p className="text-sm text-slate-700">
            The Microsoft CMT service was used for managing the peer-reviewing process for this conference. This service was provided for free by Microsoft and they bore all expenses, including costs for Azure cloud services as well as for software development and support.
          </p>
        </div>

        <ul className="text-sm text-slate-600 space-y-2">
          <li>✔ Original and unpublished work only</li>
          <li>✔ Follow double-blind review process</li>
          <li>✔ Submit before deadline</li>
        </ul>
      </div>

      <div className="mt-6 space-y-3">
        <a
          href="https://cmt3.research.microsoft.com/"
          target="_blank"
          className="block w-full text-center bg-[#B6671D] text-white py-3 rounded-lg font-semibold hover:bg-[#9A571A] transition"
        >
          Submit via Microsoft CMT
        </a>

        <a
          href="/paper-format.pdf"
          className="block w-full text-center border border-slate-300 py-3 rounded-lg font-semibold hover:bg-slate-100 transition"
        >
          Download Template
        </a>
      </div>
      

    </div>

    
  </motion.div>

     <div>
      <h2 className="text-2xl font-bold text-slate-800">
        Ethics and Malpractice Statement
      </h2>

      <p className="text-slate-600 leading-relaxed">
        The International Conference on Robotics, Automation and Smart Manufacturing (IRASM-2027) is committed to upholding the highest standards of academic integrity, ethical conduct, and professionalism in research and publication. This statement outlines the ethical responsibilities of authors, reviewers, committee members, and conference organizers.
      </p>
     </div>
      {/* Section */}
      <div>
        <h3 className="font-semibold text-lg text-slate-800">Purpose</h3>
        <p className="text-slate-600">
          IRASM 2027 aims to promote ethical research practices and ensure trust, transparency, and accountability throughout the submission, review, and publication process.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Originality and Authorship</h3>
        <p className="text-slate-600">
          Submitted manuscripts must be original and must not be under review or published elsewhere. All individuals who have made a significant contribution to the research must be appropriately acknowledged as authors. All listed authors must approve the final version of the manuscript prior to submission.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Plagiarism and Research Misconduct</h3>
        <p className="text-slate-600">
          Plagiarism, data fabrication, data falsification, duplicate submission, and misrepresentation of research findings are strictly prohibited. Manuscripts may be screened using plagiarism detection tools. Any confirmed instance of research misconduct may result in rejection, withdrawal of the paper, or retraction from the conference proceedings.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Peer Review Process</h3>
        <p className="text-slate-600">
          All eligible submissions to IRASM 2027 undergo a fair, objective, and unbiased peer review process. Reviewers evaluate manuscripts based on technical merit, originality, relevance to conference themes, and clarity of presentation.
        </p>
      </div>
    

    
      <div>
        <h3 className="font-semibold text-lg text-slate-800">Conflicts of Interest</h3>
        <p className="text-slate-600">
          Authors, reviewers, and committee members must disclose any potential or actual conflicts of interest that could influence their judgment or decision-making during the review and publication process.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Confidentiality</h3>
        <p className="text-slate-600">
          All submitted manuscripts and review reports are treated as confidential. Reviewers and committee members must not disclose, discuss, or use any information related to manuscripts under review for personal or professional advantage.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Ethical Compliance</h3>
        <p className="text-slate-600">
          Research involving human participants, animals, or sensitive data must comply with applicable ethical standards, regulations, and institutional guidelines. Authors are responsible for obtaining and declaring all necessary ethical approvals where required.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-lg text-slate-800">Policy Enforcement</h3>
        <p className="text-slate-600">
          Any violation of this ethics and malpractice policy may result in rejection of the manuscript, removal from the conference proceedings, exclusion from the conference, and notification to relevant institutions or authorities, as deemed appropriate by the conference organizers.
        </p>
      </div>
      
       <div>
        <h3 className="font-semibold text-lg text-slate-800">Commitment to Integrity</h3>
        <p className="text-slate-600">
          IRASM 2027 is dedicated to fostering a respectful, ethical, and transparent research environment that advances innovation and scholarly excellence.
        </p>
      </div>  

</div>
            </TabsContent>

            {/* Registration Tab */}
            <TabsContent value="registration" className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Fees Table */}
                <motion.div {...fadeInUp}>
                  <h2 className="font-outfit text-3xl font-bold text-slate-950 mb-6">Registration Fees</h2>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left font-outfit font-bold text-slate-950">Category</th>
                          <th className="px-6 py-4 text-right font-outfit font-bold text-slate-950">Fee</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {registrationFees.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50" data-testid={`fee-row-${index}`}>
                            <td className="px-6 py-4 font-inter text-slate-700">{item.category}</td>
                            <td className="px-6 py-4 text-right font-mono text-lg font-semibold text-[#B6671D]">{item.fee}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="font-inter text-sm text-slate-600 mt-4">
                    * Early bird registration available until 31 December 2026.
                  </p>
                </motion.div>

                {/* Registration Form */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-8">
                    <h3 className="font-outfit text-2xl font-bold text-slate-950 mb-6">Register Now</h3>
                    <form onSubmit={handleRegSubmit} className="space-y-5">
                      <div>
                        <Label htmlFor="full_name">Full Name *</Label>
                        <Input id="full_name" name="full_name" value={regFormData.full_name} onChange={handleRegChange} required className="mt-1" data-testid="reg-name-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reg_email">Email *</Label>
                          <Input id="reg_email" name="email" type="email" value={regFormData.email} onChange={handleRegChange} required className="mt-1" data-testid="reg-email-input" />
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone *</Label>
                          <Input id="phone" name="phone" value={regFormData.phone} onChange={handleRegChange} required className="mt-1" data-testid="reg-phone-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="category">Registration Category *</Label>
                        <Select value={regFormData.category} onValueChange={(v) => setRegFormData({...regFormData, category: v})}>
                          <SelectTrigger className="mt-1" data-testid="reg-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {registrationFees.map((item) => (<SelectItem key={item.category} value={item.category}>{item.category} - {item.fee}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reg_affiliation">Affiliation *</Label>
                          <Input id="reg_affiliation" name="affiliation" value={regFormData.affiliation} onChange={handleRegChange} required className="mt-1" data-testid="reg-affiliation-input" />
                        </div>
                        <div>
                          <Label htmlFor="country">Country *</Label>
                          <Input id="country" name="country" value={regFormData.country} onChange={handleRegChange} required className="mt-1" data-testid="reg-country-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="dietary_requirements">Dietary Requirements</Label>
                        <Input id="dietary_requirements" name="dietary_requirements" value={regFormData.dietary_requirements} onChange={handleRegChange} className="mt-1" data-testid="reg-dietary-input" />
                      </div>
                      <Button type="submit" disabled={isSubmittingReg} className="w-full bg-slate-950 hover:bg-slate-800 text-white py-5" data-testid="reg-submit-button">
                        {isSubmittingReg ? 'Processing...' : <><Send className="mr-2" size={18} />Complete Registration</>}
                      </Button>
                    </form>
                  </div>
                </motion.div>
              </div>
            </TabsContent>

            {/* Conference Brochure Tab */}
            <TabsContent value="brochure" className="mt-8">
              <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
                <h2 className="font-outfit text-3xl font-bold text-slate-950 mb-6 text-center">Conference Brochure</h2>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
                  <div className="w-24 h-24 bg-[#B6671D]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Download className="text-[#B6671D]" size={40} />
                  </div>
                  <h3 className="font-outfit text-2xl font-bold text-slate-950 mb-4">Download Conference Brochure</h3>
                  <p className="font-inter text-slate-600 mb-8 max-w-lg mx-auto">
                    Get all the essential information about IRASM-2027 including conference tracks, important dates, registration details, and venue information in a downloadable PDF format.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Button className="bg-[#B6671D] hover:bg-[#9A571A] text-white px-8 py-5" data-testid="download-brochure-btn">
                      <Download className="mr-2" size={20} />
                      Download Brochure (PDF)
                    </Button>
                    <Button variant="outline" className="border-2 border-slate-950 text-slate-950 px-8 py-5" data-testid="view-brochure-btn">
                      <ExternalLink className="mr-2" size={20} />
                      View Online
                    </Button>
                  </div>
                  <p className="font-inter text-sm text-slate-500 mt-6">
                    * Brochure will be available for download soon.
                  </p>
                </div>
              </motion.div>
            </TabsContent>

            {/* Accommodation Tab */}
            <TabsContent value="accommodation" className="mt-8">
              <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
                <h2 className="font-outfit text-3xl font-bold text-slate-950 mb-6">Accommodation</h2>
                <div className="space-y-6">
                  <p className="font-inter text-slate-600 leading-relaxed">
                    Manipal University Jaipur and the conference organizing committee will assist participants in finding suitable accommodation near the venue. Options include:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all">
                      <div className="w-12 h-12 bg-[#B6671D]/10 rounded-lg flex items-center justify-center mb-4">
                        <Hotel className="text-[#B6671D]" size={24} />
                      </div>
                      <h3 className="font-outfit text-xl font-bold text-slate-950 mb-2">University Guest House</h3>
                      <p className="font-inter text-slate-600 text-sm">
                        On-campus accommodation available for participants. Limited rooms available on first-come, first-served basis.
                      </p>
                    </div>
                    
                    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-all">
                      <div className="w-12 h-12 bg-[#B6671D]/10 rounded-lg flex items-center justify-center mb-4">
                        <Hotel className="text-[#B6671D]" size={24} />
                      </div>
                      <h3 className="font-outfit text-xl font-bold text-slate-950 mb-2">Partner Hotels</h3>
                      <p className="font-inter text-slate-600 text-sm">
                        Discounted rates at partner hotels near the venue. Details will be shared with registered participants.
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#B6671D]/10 border-l-4 border-[#B6671D] p-6 rounded-r-lg">
                    <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">Transportation</h4>
                    <p className="font-inter text-slate-700">
                      The organizing committee will arrange transportation from Jaipur Railway Station and Jaipur International Airport to the venue on conference days. Details will be communicated to registered participants.
                    </p>
                  </div>

                  <p className="font-inter text-sm text-slate-500">
                    * Detailed accommodation information and booking links will be available after registration opens.
                  </p>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default ForAuthors;
